import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import '../styles/compsStyles/photoEditor.css'

const FILTERS = [
  { id: 'original', label: 'Original', css: 'none' },
  { id: 'clarendon', label: 'Clarendon', css: 'contrast(1.2) saturate(1.35)' },
  { id: 'gingham', label: 'Gingham', css: 'brightness(1.05) hue-rotate(-10deg) saturate(0.85)' },
  { id: 'moon', label: 'Moon', css: 'grayscale(1) contrast(1.1) brightness(1.1)' },
  { id: 'lark', label: 'Lark', css: 'contrast(0.9) brightness(1.12) saturate(1.2)' },
  { id: 'reyes', label: 'Reyes', css: 'sepia(0.22) brightness(1.1) contrast(0.85)' },
  { id: 'juno', label: 'Juno', css: 'contrast(1.15) saturate(1.4) hue-rotate(-8deg)' },
  { id: 'valencia', label: 'Valencia', css: 'sepia(0.15) contrast(1.08) brightness(1.08) saturate(1.2)' },
  { id: 'ludwig', label: 'Ludwig', css: 'contrast(1.05) saturate(0.8) brightness(1.05)' },
  { id: 'aden', label: 'Aden', css: 'hue-rotate(-20deg) contrast(0.9) saturate(0.85) brightness(1.15)' }
]

const ASPECTS = [
  { id: 'free', label: 'Original' },
  { id: '1:1', label: '1:1', ratio: 1 },
  { id: '4:5', label: '4:5', ratio: 4 / 5 },
  { id: '16:9', label: '16:9', ratio: 16 / 9 }
]

const MAX_OUT = 2048

const loadImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file)
  const img = new Image()
  img.onload = () => resolve({ img, url })
  img.onerror = () => {
    URL.revokeObjectURL(url)
    reject(new Error('Could not read this photo.'))
  }
  img.src = url
})

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

const PhotoEditor = ({ file, onCancel, onApply }) => {
  const stageRef = useRef(null)
  const cropRef = useRef(null)
  const dragRef = useRef(null)
  const pinchRef = useRef(null)
  const [src, setSrc] = useState('')
  const [image, setImage] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [filterId, setFilterId] = useState('original')
  const [aspectId, setAspectId] = useState('1:1')
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [frame, setFrame] = useState(0)

  const filterCss = useMemo(
    () => (FILTERS.find((item) => item.id === filterId) || FILTERS[0]).css,
    [filterId]
  )
  const aspect = ASPECTS.find((item) => item.id === aspectId) || ASPECTS[1]
  const isFree = aspect.id === 'free'

  useEffect(() => {
    let revoked = ''
    setError('')
    setFilterId('original')
    setAspectId('1:1')
    setZoom(1)
    setPan({ x: 0, y: 0 })
    setImage(null)
    loadImage(file)
      .then(({ img, url }) => {
        revoked = url
        setSrc(url)
        setImage(img)
      })
      .catch((err) => setError(err.message || 'Could not open this photo.'))
    return () => {
      if (revoked) URL.revokeObjectURL(revoked)
    }
  }, [file])

  useLayoutEffect(() => {
    setFrame((n) => n + 1)
  }, [image, aspectId])

  const sizes = useCallback(() => {
    const stage = stageRef.current
    const img = image
    if (!stage || !img) return null
    const stageW = stage.clientWidth
    const stageH = stage.clientHeight
    if (!stageW || !stageH) return null
    let cw = stageW
    let ch = stageH
    if (aspect.ratio) {
      cw = stageW
      ch = cw / aspect.ratio
      if (ch > stageH) {
        ch = stageH
        cw = ch * aspect.ratio
      }
    }
    const cover = Math.max(cw / img.naturalWidth, ch / img.naturalHeight)
    const dw = img.naturalWidth * cover * zoom
    const dh = img.naturalHeight * cover * zoom
    const maxX = Math.max(0, (dw - cw) / 2)
    const maxY = Math.max(0, (dh - ch) / 2)
    return { cw, ch, dw, dh, maxX, maxY, cover }
  }, [image, zoom, frame, aspect.ratio])

  const layout = sizes()
  const clampedPan = layout
    ? { x: clamp(pan.x, -layout.maxX, layout.maxX), y: clamp(pan.y, -layout.maxY, layout.maxY) }
    : pan

  const setZoomSafe = (nextZoom) => {
    setZoom(clamp(nextZoom, 1, 4))
  }

  const pointerDown = (event) => {
    if (isFree || event.button) return
    if (event.touches && event.touches.length === 2) {
      const [a, b] = event.touches
      pinchRef.current = {
        dist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        zoom
      }
      dragRef.current = null
      return
    }
    const point = event.touches ? event.touches[0] : event
    dragRef.current = { x: point.clientX, y: point.clientY, pan: clampedPan }
  }

  const pointerMove = (event) => {
    if (isFree) return
    if (event.touches && event.touches.length === 2 && pinchRef.current) {
      event.preventDefault()
      const [a, b] = event.touches
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
      setZoomSafe(pinchRef.current.zoom * (dist / pinchRef.current.dist))
      return
    }
    if (!dragRef.current) return
    const point = event.touches ? event.touches[0] : event
    const dx = point.clientX - dragRef.current.x
    const dy = point.clientY - dragRef.current.y
    const next = sizes()
    const x = dragRef.current.pan.x + dx
    const y = dragRef.current.pan.y + dy
    setPan(next
      ? { x: clamp(x, -next.maxX, next.maxX), y: clamp(y, -next.maxY, next.maxY) }
      : { x, y })
  }

  const pointerUp = () => {
    dragRef.current = null
    pinchRef.current = null
  }

  const onWheel = (event) => {
    if (isFree) return
    event.preventDefault()
    setZoomSafe(zoom + (event.deltaY < 0 ? 0.08 : -0.08))
  }

  const apply = async () => {
    if (!image) return
    setBusy(true)
    setError('')
    try {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      ctx.filter = filterCss === 'none' ? 'none' : filterCss
      if (isFree) {
        const scale = Math.min(1, MAX_OUT / Math.max(image.naturalWidth, image.naturalHeight))
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale))
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale))
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
      } else {
        const next = sizes()
        if (!next) throw new Error('Could not crop this photo.')
        const sx = ((next.dw / 2) - next.cw / 2 - clampedPan.x) / next.dw * image.naturalWidth
        const sy = ((next.dh / 2) - next.ch / 2 - clampedPan.y) / next.dh * image.naturalHeight
        const sw = next.cw / next.dw * image.naturalWidth
        const sh = next.ch / next.dh * image.naturalHeight
        const outScale = Math.min(1, MAX_OUT / Math.max(sw, sh))
        canvas.width = Math.max(1, Math.round(sw * outScale))
        canvas.height = Math.max(1, Math.round(sh * outScale))
        ctx.drawImage(image, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height)
      }
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (next) => (next ? resolve(next) : reject(new Error('Could not save this photo.'))),
          'image/jpeg',
          0.92
        )
      })
      const base = String(file.name || 'photo').replace(/\.[^.]+$/, '')
      onApply(new File([blob], `${base}.jpg`, { type: 'image/jpeg', lastModified: Date.now() }))
    } catch (err) {
      setError(err.message || 'Could not save this photo.')
      setBusy(false)
    }
  }

  const cropStyle = layout
    ? { width: `${layout.cw}px`, height: `${layout.ch}px`, aspectRatio: 'auto' }
    : aspect.ratio
      ? { aspectRatio: String(aspect.ratio), width: '100%' }
      : { width: '100%', height: '100%' }

  return (
    <div className="photoEditorOverlay" role="dialog" aria-labelledby="photoEditorTitle">
      <div className="photoEditorCard">
        <header className="photoEditorBar">
          <button type="button" onClick={onCancel} disabled={busy}>Cancel</button>
          <h2 id="photoEditorTitle">Edit photo</h2>
          <button type="button" className="isPrimary" onClick={apply} disabled={busy || !image}>
            {busy ? 'Saving…' : 'Done'}
          </button>
        </header>

        <div
          ref={stageRef}
          className={`photoEditorStage${isFree ? ' isFree' : ''}`}
          onMouseDown={pointerDown}
          onMouseMove={pointerMove}
          onMouseUp={pointerUp}
          onMouseLeave={pointerUp}
          onTouchStart={pointerDown}
          onTouchMove={pointerMove}
          onTouchEnd={pointerUp}
          onWheel={onWheel}
        >
          <div ref={cropRef} className="photoEditorCrop" style={cropStyle}>
            {src ? (
              <img
                src={src}
                alt=""
                draggable={false}
                style={{
                  filter: filterCss,
                  width: isFree || !layout ? '100%' : `${layout.dw}px`,
                  height: isFree || !layout ? '100%' : `${layout.dh}px`,
                  transform: isFree
                    ? 'translate(-50%, -50%)'
                    : `translate(calc(-50% + ${clampedPan.x}px), calc(-50% + ${clampedPan.y}px))`
                }}
              />
            ) : (
              <div className="photoEditorLoading">Opening photo…</div>
            )}
            {!isFree && <span className="photoEditorGrid" aria-hidden="true" />}
          </div>
        </div>

        {error ? <p className="photoEditorError">{error}</p> : null}

        <div className="photoEditorTools">
          <div className="photoEditorAspects" role="tablist" aria-label="Crop">
            {ASPECTS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={aspectId === item.id}
                className={aspectId === item.id ? 'isActive' : ''}
                onClick={() => {
                  setAspectId(item.id)
                  setZoom(1)
                  setPan({ x: 0, y: 0 })
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
          {!isFree && (
            <label className="photoEditorZoom">
              <span>Zoom</span>
              <input
                type="range"
                min="1"
                max="4"
                step="0.01"
                value={zoom}
                onChange={(event) => setZoomSafe(Number(event.target.value))}
              />
            </label>
          )}
          <div className="photoEditorFilters" aria-label="Filters">
            {FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={filterId === item.id ? 'isActive' : ''}
                onClick={() => setFilterId(item.id)}
              >
                <span className="photoEditorFilterThumb">
                  {src ? <img src={src} alt="" style={{ filter: item.css }} /> : null}
                </span>
                <small>{item.label}</small>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PhotoEditor
